import { useMemo } from "react";
import { Link } from "react-router";
import { MultiSelect, type Option } from "@/components/ui/multi-select";
import { useCustomers } from "@/customers/useCustomers";
import { ROUTES as RAW_FILES_VIEWER_ROUTES } from "@/raw-files-viewer/navigation";
import { useProviderFilter } from "./useProviderFilter";
import { useRelevanceReviewQueue } from "./useRelevanceReviewQueue";

export default function RelevanceReviewQueuePage() {
  const { customers, isLoading: customersLoading } = useCustomers();
  const { providerIds, setProviderIds } = useProviderFilter();
  const { data: encounters = [], isLoading, error } = useRelevanceReviewQueue({ providerIds });

  const providerOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: Option[] = [];
    for (const customer of customers) {
      if (seen.has(customer.providerId)) continue;
      seen.add(customer.providerId);
      options.push({ value: customer.providerId, label: customer.providerId });
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [customers]);

  const selectedProviders = useMemo(
    () => providerOptions.filter((option) => providerIds.includes(option.value)),
    [providerOptions, providerIds],
  );

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b pb-3">
        <div className="flex items-baseline gap-2">
          <h1 className="text-base font-semibold">Relevance review queue</h1>
          <span className="text-sm text-muted-foreground">
            Encounters with encounter-relevance verdicts pending human review.
          </span>
        </div>

        <div className="w-full sm:w-64 sm:shrink-0">
          <MultiSelect
            options={providerOptions}
            value={selectedProviders}
            onChange={(selected) => setProviderIds(selected.map((option) => option.value))}
            placeholder="All providers"
            isLoading={customersLoading}
          />
        </div>
      </div>

      {isLoading && <div className="text-sm text-muted-foreground">Loading queue…</div>}
      {error && <div className="text-destructive">{error.message}</div>}
      {!isLoading && !error && encounters.length === 0 && (
        <div className="text-sm text-muted-foreground">No pending relevance reviews.</div>
      )}

      {encounters.length > 0 && (
        <ul className="divide-y rounded-md border">
          {encounters.map((encounter) => (
            <li key={encounter.encounterId}>
              <Link
                to={`${RAW_FILES_VIEWER_ROUTES.rawFilesViewer.route}/${encounter.encounterId}`}
                className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-muted"
              >
                <span className="font-mono text-sm">{encounter.encounterId}</span>
                <span className="text-sm text-muted-foreground">
                  {encounter.pendingCount} pending · oldest {new Date(encounter.oldestPendingAt).toLocaleDateString()}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
